import { applyDefaultProfileName, getProfileCanonicalName } from './profile-state-utils';

export type ModuleProfileSource = 'TENANT' | 'COMPANY' | 'USER';

type ProfilesJsonList = (string | null | undefined)[];

type ParsedModuleSource = {
  profiles: any[];
  filters: any[];
  defaultProfileName?: string;
};

type ModuleProfileHierarchyInput = {
  tenantProfilesJson?: ProfilesJsonList;
  tenantFiltersJson?: ProfilesJsonList;
  companyProfilesJson?: ProfilesJsonList;
  userProfilesJson?: ProfilesJsonList;
};

type DuplicateNameOptions = {
  preserveOriginalName?: boolean;
};

type EffectiveProfileListInput = {
  hierarchy?: ReturnType<typeof collectModuleProfileHierarchy> | null;
  localProfiles?: any[];
  defaultProfileName?: string;
  localSource?: ModuleProfileSource;
  localExtraProps?: Record<string, any>;
  inheritedExtraPropsBySource?: Partial<Record<ModuleProfileSource, Record<string, any>>>;
  preserveOriginalName?: boolean;
};

const PROFILE_SOURCE_META: Record<
  ModuleProfileSource,
  { source: ModuleProfileSource; color: string; labelTooltip: string; readOnly: boolean }
> = {
  TENANT: {
    source: 'TENANT',
    color: 'lightblue',
    labelTooltip: 'Tenant Profile',
    readOnly: true,
  },
  COMPANY: {
    source: 'COMPANY',
    color: 'lightyellow',
    labelTooltip: 'Company Profile',
    readOnly: true,
  },
  USER: {
    source: 'USER',
    color: 'lightgreen',
    labelTooltip: 'User Profile',
    readOnly: false,
  },
};

function parseEnabledModuleSource(
  profilesJsonList: ProfilesJsonList | undefined,
  moduleId: string,
): ParsedModuleSource {
  const profiles: any[] = [];
  const filters: any[] = [];
  let defaultProfileName: string | undefined;

  profilesJsonList?.forEach((profilesJson) => {
    if (!profilesJson) return;

    let parsed: any[] = [];
    try {
      const value = JSON.parse(profilesJson ?? '[]');
      parsed = Array.isArray(value) ? value : [];
    } catch {
      parsed = [];
    }

    const moduleConfig = parsed.find((item: any) => item?.id === moduleId && item?.enabled);
    if (!moduleConfig) return;

    if (!defaultProfileName && moduleConfig.defaultProfile) {
      defaultProfileName = moduleConfig.defaultProfile;
    }

    if (Array.isArray(moduleConfig.profiles)) {
      profiles.push(...moduleConfig.profiles);
    }

    if (Array.isArray(moduleConfig.filters)) {
      filters.push(...moduleConfig.filters);
    }
  });

  return {
    profiles,
    filters,
    defaultProfileName,
  };
}

export function collectModuleProfileHierarchy(
  moduleId: string,
  input: ModuleProfileHierarchyInput,
) {
  const tenant = parseEnabledModuleSource(input.tenantProfilesJson, moduleId);
  const tenantFilters = parseEnabledModuleSource(
    input.tenantFiltersJson ?? input.tenantProfilesJson,
    moduleId,
  );
  const company = parseEnabledModuleSource(input.companyProfilesJson, moduleId);
  const user = parseEnabledModuleSource(input.userProfilesJson, moduleId);

  return {
    profilesBySource: {
      TENANT: tenant.profiles,
      COMPANY: company.profiles,
      USER: user.profiles,
    },
    filtersBySource: {
      TENANT: tenantFilters.filters,
      COMPANY: company.filters,
      USER: user.filters,
    },
    defaultProfileNameBySource: {
      TENANT: tenant.defaultProfileName,
      COMPANY: company.defaultProfileName,
      USER: user.defaultProfileName,
    },
  };
}

export function decorateProfilesForSource(
  profiles: any[] | undefined,
  source: ModuleProfileSource,
  extraProps?: Record<string, any>,
) {
  const meta = PROFILE_SOURCE_META[source];

  return (profiles ?? []).map((profile: any) => ({
    ...profile,
    ...meta,
    ...(extraProps ?? {}),
  }));
}

export function addNumbersToDuplicateProfileNames<T extends { name?: string }>(
  items: T[] | undefined,
  options?: DuplicateNameOptions,
): (T & { originalName?: string })[] {
  const counts = new Map<string, number>();

  return (items ?? []).map((item) => {
    const currentName = item?.name ?? '';
    const seen = counts.get(currentName) ?? 0;

    counts.set(currentName, seen + 1);

    const nextName = seen === 0 ? currentName : `${currentName} (${seen + 1})`;

    return {
      ...item,
      ...(options?.preserveOriginalName ? { originalName: currentName } : {}),
      name: nextName,
    };
  });
}

export function buildEffectiveProfileList(input: EffectiveProfileListInput) {
  const inheritedProfiles = [
    ...decorateProfilesForSource(
      input.hierarchy?.profilesBySource.TENANT,
      'TENANT',
      input.inheritedExtraPropsBySource?.TENANT,
    ),
    ...decorateProfilesForSource(
      input.hierarchy?.profilesBySource.COMPANY,
      'COMPANY',
      input.inheritedExtraPropsBySource?.COMPANY,
    ),
  ];

  const localProfiles = decorateProfilesForSource(
    input.localProfiles,
    input.localSource ?? 'USER',
    input.localExtraProps,
  );

  return applyDefaultProfileName(
    addNumbersToDuplicateProfileNames([...inheritedProfiles, ...localProfiles], {
      preserveOriginalName: input.preserveOriginalName,
    }),
    input.defaultProfileName,
    (profile) => getProfileCanonicalName(profile),
  );
}
